import type { RiskRatingAdjustmentModalProps } from '../../types/modals'
import { useEffect, useMemo, useState } from 'react'
import { CreditCard, Home, KeyRound, ShieldCheck } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { updateRiskRating } from '../../services/applicationService'
import { showError, showSuccess } from '@/lib/utils/notifications'
import {
  computeRiskRatingPopupAmounts,
  RISK_RATING_OPTIONS,
  resolveRiskPercentages,
} from '../../utils/riskRatingAccessFee'
import styles from '../../styles/applicationDetail.module.css'
import modalStyles from '../../styles/modals.module.css'


const tid = 'application-modal-risk-rating-adjustment'

const SUMMARY_ROWS = [
  { key: 'rent', label: 'Rent Amount' },
  { key: 'access', label: 'Access Fee' },
  { key: 'balance', label: 'Balance Due' },
] as const

export function RiskRatingAdjustmentModal({
  isOpen,
  onClose,
  applicantId,
  applicationAllData,
  onSuccess,
}: RiskRatingAdjustmentModalProps) {
  const [riskRating, setRiskRating] = useState('MEDIUM')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const riskPercentages = useMemo(
    () => resolveRiskPercentages(applicationAllData?.riskPercentages),
    [applicationAllData?.riskPercentages],
  )

  const ratingOptions = useMemo(() => {
    const keys = Object.keys(riskPercentages)
    if (keys.length === 0) {
      return RISK_RATING_OPTIONS.map((o) => o.value)
    }
    const preferred = ['LOW', 'MEDIUM', 'HIGH']
    const ordered = preferred.filter((k) => keys.includes(k))
    const rest = keys.filter((k) => !preferred.includes(k))
    return [...ordered, ...rest]
  }, [riskPercentages])

  const amounts = useMemo(() => {
    if (!applicationAllData) {
      return {
        rentAmount: '0.00',
        oldAccessFee: '0.00',
        newAccessFee: '0.00',
        accessFeeChanged: false,
      }
    }
    return computeRiskRatingPopupAmounts(applicationAllData, riskRating)
  }, [applicationAllData, riskRating])

  useEffect(() => {
    if (!isOpen || !applicationAllData) return
    const current = String(applicationAllData.risk_rating ?? 'MEDIUM')
    setRiskRating(ratingOptions.includes(current) ? current : ratingOptions[0] ?? 'MEDIUM')
    setNote('')
  }, [isOpen, applicationAllData, ratingOptions])

  if (!applicationAllData) return null

  const applicationId = applicationAllData.application_id as string | number | undefined
  if (applicationId == null) return null

  const currentRiskRating = String(applicationAllData.risk_rating ?? '')

  const submit = async () => {
    const trimmedNote = note.trim()
    if (!trimmedNote) {
      showError('Note is required.')
      return
    }
    if (currentRiskRating === riskRating) {
      onClose()
      return
    }
    setSaving(true)
    try {
      await updateRiskRating(applicationId, {
        risk_rating: riskRating,
        note: trimmedNote,
        applicant_id: applicantId,
      })
      showSuccess('Risk Rating Changed Successfully!')
      onSuccess()
      onClose()
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to update risk rating.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Risk Rating Adjustment"
      onClose={onClose}
      maxWidth="max-w-2xl"
      bodyClassName={styles.riskRatingModalBody}
      bodyScrollOnly
      footer={
        <div className={modalStyles.modalFooterActionGroup} data-testid={`${tid}-footer`}>
          <button
            type="button"
            className={modalStyles.modalActionPrimary}
            disabled={saving}
            onClick={() => void submit()}
            data-testid={`${tid}-confirm-button`}
          >
            {saving ? 'Saving…' : 'Confirm'}
          </button>
          <button
            type="button"
            className={modalStyles.modalActionSecondary}
            onClick={onClose}
            data-testid={`${tid}-cancel-btn`}
          >
            Cancel
          </button>
        </div>
      }
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
    >
      <form
        className={`${styles.riskRatingModalForm} ${styles.riskRatingLayout4}`}
        data-testid={`${tid}-body`}
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <div className={styles.riskRatingHero}>
          <label
            htmlFor={`${tid}-risk-rating-input`}
            className={styles.riskRatingSelectLabel}
            data-testid={`${tid}-risk-rating-label`}
          >
            Application Risk Rating
          </label>
          <div className={styles.riskRatingSelectWrap}>
            <ShieldCheck className={styles.riskRatingSelectIcon} aria-hidden />
            <select
              id={`${tid}-risk-rating-input`}
              className={styles.riskRatingSelect}
              value={riskRating}
              onChange={(e) => setRiskRating(e.target.value)}
              data-testid={`${tid}-risk-rating-input`}
            >
              {ratingOptions.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          className={styles.riskRatingSummaryGrid}
          data-testid={`${tid}-summary-table`}
        >
          {SUMMARY_ROWS.map((row) => {
            const Icon =
              row.key === 'rent' ? Home : row.key === 'access' ? KeyRound : CreditCard

            const value =
              row.key === 'rent'
                ? amounts.rentAmount
                : row.key === 'balance'
                  ? amounts.newAccessFee
                  : null

            return (
              <div key={row.key} className={styles.riskRatingSummaryCard}>
                <span className={styles.riskRatingSummaryIcon} aria-hidden>
                  <Icon className="h-5 w-5" />
                </span>
                <span className={styles.riskRatingSummaryLabel}>{row.label}</span>
                {row.key === 'access' && amounts.accessFeeChanged ? (
                  <div
                    className={styles.riskRatingSummaryValue}
                    data-testid={`${tid}-summary-access`}
                  >
                    <div className={styles.riskRatingAccessFeePair}>
                      <span className={styles.riskRatingAccessFeeColumn}>
                        <span className={styles.riskRatingAccessFeeMeta}>Old ($)</span>
                        <strong data-testid={`${tid}-summary-access-old`}>
                          {amounts.oldAccessFee}
                        </strong>
                      </span>
                      <span className={styles.riskRatingAccessFeeColumn}>
                        <span className={styles.riskRatingAccessFeeMeta}>New ($)</span>
                        <strong data-testid={`${tid}-summary-access-new`}>
                          {amounts.newAccessFee}
                        </strong>
                      </span>
                    </div>
                  </div>
                ) : (
                  <strong
                    className={styles.riskRatingSummaryValue}
                    data-testid={`${tid}-summary-${row.key}`}
                  >
                    {row.key === 'access' ? amounts.oldAccessFee : value}
                  </strong>
                )}
              </div>
            )
          })}
        </div>

        <div className={styles.riskRatingNoteGroup}>
          <label
            htmlFor={`${tid}-note-input`}
            className={styles.riskRatingNoteLabel}
            data-testid={`${tid}-note-label`}
          >
            Note <span className="text-red-600">*</span>
          </label>
          <textarea
            id={`${tid}-note-input`}
            className={styles.riskRatingTextarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Enter note"
            data-testid={`${tid}-note-input`}
          />
        </div>

      </form>
    </OnScreenModal>
  )
}
